import type { Restaurant } from "api-types";
import { Hint } from "ts-packages/igfd-ui/src";

import React from "react";
import { connect, useSelector } from "react-redux";

import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { CheckCircleRounded, RestaurantRounded } from "@mui/icons-material";

import { NewRestaurantModal } from "@dashboard/components/restaurant/create/NewRestaurantModal";
import { useDisclosure } from "@dashboard/hooks/useDisclosure";

import { restaurantSelectedAction } from "../../store/actions/restaurantActions";
import type { ReduxState } from "../../types/reduxState";

type RestaurantModalProps = {
  open: boolean;
  onClose: () => void;
  onNewRestaurantCreated: () => void;

  accessibleRestaurants: Restaurant[];
  selectedRestaurant: Restaurant | null;
  userIsAdmin: boolean;

  restaurantSelectedAction: (restaurant: Restaurant) => void;
};

const RestaurantModalComponent = ({
  open,
  onClose,
  onNewRestaurantCreated,

  accessibleRestaurants,
  selectedRestaurant,
  userIsAdmin,
  restaurantSelectedAction,
}: RestaurantModalProps) => {
  const {
    isOpen: addRestaurantModalOpen,
    open: openAddRestaurantModal,
    close: closeAddRestaurantModal,
  } = useDisclosure();

  const onNewRestaurantCreatedCallback = () => {
    onNewRestaurantCreated();
    closeAddRestaurantModal();
    onClose();
  };

  const BodyComponent = () => (
    <>
      <Typography variant={"h3"}>Select a restaurant</Typography>
      <List component="a">
        {userIsAdmin && (
          <Hint
            severity="error"
            hintValue={
              "You are an Admin!! Please use the Admin Mode Button for changing restaurants. Contact #development in case of issues."
            }
          />
        )}

        {!userIsAdmin &&
          accessibleRestaurants.map((restaurant) => (
            <RestaurantListItemButton
              key={restaurant.uuid}
              selected={selectedRestaurant?.uuid === restaurant.uuid}
              onClick={() => {
                restaurantSelectedAction(restaurant);
                onClose();
              }}
            >
              <ListItemText primary={restaurant.name} />
              {selectedRestaurant &&
                restaurant.uuid === selectedRestaurant.uuid && (
                  <CheckCircleRounded color={"secondary"} fontSize={"small"} />
                )}
            </RestaurantListItemButton>
          ))}
        <RestaurantListItemButton onClick={openAddRestaurantModal}>
          <ListItemText primary={"+ Add new restaurant"} />
        </RestaurantListItemButton>
      </List>

      <NewRestaurantModal
        open={addRestaurantModalOpen}
        onNewRestaurantCreated={onNewRestaurantCreatedCallback}
        onClose={closeAddRestaurantModal}
      />
    </>
  );

  return (
    <Modal open={open} onClose={(_) => onClose()} disableEnforceFocus>
      <ModalBox>
        <ModalContainer>
          <IconContainer>
            <RestaurantRounded fontSize={"large"} />
          </IconContainer>
          <BottomContainer>
            <BodyComponent />
          </BottomContainer>
        </ModalContainer>
      </ModalBox>
    </Modal>
  );
};

const RestaurantListItemButton = styled(ListItemButton)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  borderRadius: 1,
  gap: theme.spacing(1),
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 350,
  maxWidth: "100%",
  maxHeight: "100%",
  overflow: "scroll",
  backgroundColor: theme.palette.grey[50],
  borderRadius: 24,
}));

const ModalContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  alignItems: "center",
  spacing: theme.spacing(3),
}));

const IconContainer = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[50],
  padding: "48px 0 32px 0",
}));

const BottomContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  width: "100%",
  padding: "32px",
  gap: theme.spacing(3),
  alignItems: "center",
  textAlign: "center",
}));

export const RestaurantModal = connect(
  (state: ReduxState) => ({
    accessibleRestaurants: state.restaurant.accessibleRestaurants,
    selectedRestaurant: state.restaurant.selectedRestaurant,
    userIsAdmin: state.dashboardApp.userIsAdmin,
  }),
  {
    restaurantSelectedAction,
  }
)(RestaurantModalComponent);
